/* =============================================================================
   Collection Compare (SD-26-557)

   Opt-in (`enable_compare` on sections/collection.liquid). Responsibilities:
   - Selection store: sessionStorage key `collection-compare:<collection handle>`
     (try/catch with an in-memory fallback). Items are {handle, title, image}.
   - Compare checkboxes on cards: cap of 3 at >=750px and 2 below; at the cap
     the remaining checkboxes are disabled and a hint is announced.
   - Sticky tray: chips + dashed empty slots + Compare button (enabled at >=2).
   - Full-screen <dialog>: showModal(), scroll lock, `#compare=h1,h2,h3` hash +
     popstate, focus to the "Compare Products" h2 and back to the Compare button.
     Slides in from the right (CSS); closing plays the slide-out first.
   - With 2 compared on desktop, the fragment adds a dashed "add another" slot
     whose select (data-current="") appends a third product via onSwap.
   - The dialog body is NEVER built here. It is fetched from the Section
     Rendering API (sections/collection-compare.liquid, see its header):
       /search?q=<collection>|<h1>,<h2>,<h3>&section_id=collection-compare
     Remove and swap update the selection and re-fetch.
   - IntersectionObserver reveals the condensed sticky header once the card
     strip scrolls away.
   - Re-syncs checkbox state on the `collection:loadmore` event (collection.js).
   Add to Cart posts the native product-form to /cart/add.js (a native submit
   from the injected dialog full-page POSTs to /cart; Rebuy only intercepts
   AJAX adds), then closes the dialog so Rebuy's drawer is visible.
   ========================================================================= */

(() => {
  'use strict';

  if (window.__sdCollectionCompareInit) return;
  window.__sdCollectionCompareInit = true;

  const SECTION_ID = 'collection-compare';
  const STORAGE_PREFIX = 'collection-compare:';
  const DESKTOP_QUERY = '(min-width: 750px)';
  const MAX_DESKTOP = 3;
  const MAX_MOBILE = 2;
  const HASH_PREFIX = '#compare=';
  // Backstop for the 0.25s slide-out (collection-compare.css .is-closing).
  const CLOSE_FALLBACK_MS = 400;

  /* ── Storage (sessionStorage with in-memory fallback) ──────────────────── */
  const memoryStore = new Map();

  function storageGet(key) {
    try {
      const value = window.sessionStorage.getItem(key);
      if (value !== null) return value;
    } catch {
      /* storage blocked: fall through to memory */
    }
    return memoryStore.has(key) ? memoryStore.get(key) : null;
  }

  function storageSet(key, value) {
    memoryStore.set(key, value);
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      /* storage blocked: memory copy is enough for this page view */
    }
  }

  function fill(template, values) {
    return Object.keys(values).reduce(
      (text, token) => text.split(token).join(String(values[token])),
      template
    );
  }

  /* ── Controller (one per collection section) ───────────────────────────── */
  class CollectionCompare {
    constructor(root) {
      this.root = root;
      this.collection = root.dataset.compareCollection;
      this.tray = root.querySelector('[data-compare-tray]');
      this.slots = root.querySelector('[data-compare-slots]');
      this.openButton = root.querySelector('[data-compare-open]');
      this.hintEl = root.querySelector('[data-compare-hint]');
      this.statusEl = root.querySelector('[data-compare-status]');
      this.chipTemplate = root.querySelector('[data-compare-chip-template]');
      this.slotTemplate = root.querySelector('[data-compare-slot-template]');
      this.dialog = root.querySelector('[data-compare-dialog]');
      this.state = root.querySelector('[data-compare-state]');
      this.stateMessage = root.querySelector('[data-compare-state-message]');
      this.stateTitle = root.querySelector('[data-compare-state-title]');
      this.retryButton = root.querySelector('[data-compare-retry]');
      this.body = root.querySelector('[data-compare-body]');
      this.dialogStatus = root.querySelector('[data-compare-dialog-status]');
      this.pendingAnnouncement = null;
      const configEl = root.querySelector('[data-compare-config]');

      this.valid = Boolean(
        this.collection && this.tray && this.slots && this.openButton && this.dialog &&
        this.body && this.chipTemplate && this.slotTemplate && configEl
      );
      if (!this.valid) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch {
        this.valid = false;
        return;
      }

      this.storageKey = STORAGE_PREFIX + this.collection;
      this.mq = window.matchMedia(DESKTOP_QUERY);
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: no-preference)');
      this.closing = false;
      this.abort = new AbortController();
      this.items = this.readItems();
      this.pushed = false;
      this.returnFocus = null;
      this.fetchController = null;
      this.observer = null;
      this.lastRequest = null;

      this.bind();
      this.render();
      this.openFromHash();
    }

    get max() {
      return this.mq.matches ? MAX_DESKTOP : MAX_MOBILE;
    }

    /* Selection can exceed `max` after the viewport shrinks; only the first
       `max` are shown in the tray and compared. */
    effective() {
      return this.items.slice(0, this.max);
    }

    readItems() {
      try {
        const parsed = JSON.parse(storageGet(this.storageKey) || '[]');
        if (!Array.isArray(parsed)) return [];
        const seen = new Set();
        return parsed.filter((item) => {
          const ok = item && typeof item.handle === 'string' && item.handle && !seen.has(item.handle);
          if (ok) seen.add(item.handle);
          return ok;
        }).map((item) => ({
          handle: item.handle,
          title: String(item.title || item.handle),
          image: String(item.image || ''),
        }));
      } catch {
        return [];
      }
    }

    writeItems() {
      storageSet(this.storageKey, JSON.stringify(this.items));
    }

    /* ── Events ──────────────────────────────────────────────────────────── */
    bind() {
      const { signal } = this.abort;

      this.root.addEventListener('change', (event) => {
        const input = event.target.closest && event.target.closest('[data-compare-toggle]');
        if (input) this.onToggle(input);
        const select = event.target.closest && event.target.closest('[data-compare-select]');
        if (select && this.dialog.contains(select)) this.onSwap(select);
      }, { signal });

      this.root.addEventListener('click', (event) => {
        const target = event.target;
        if (!target.closest) return;
        const chipRemove = target.closest('[data-compare-chip-remove]');
        if (chipRemove) {
          this.onChipRemove(chipRemove.closest('[data-compare-chip]'));
          return;
        }
        if (target.closest('[data-compare-open]')) {
          this.open(this.openButton);
          return;
        }
        const cardRemove = target.closest('[data-compare-remove]');
        if (cardRemove && this.dialog.contains(cardRemove)) {
          this.onDialogRemove(cardRemove.dataset.handle);
          return;
        }
        if (target.closest('[data-compare-close]') && this.dialog.contains(target)) {
          this.requestClose();
          return;
        }
        if (target.closest('[data-compare-retry]') && this.lastRequest) {
          this.fetchFragment(this.lastRequest.handles, this.lastRequest.options);
        }
      }, { signal });

      // Add to Cart inside the dialog: Rebuy doesn't catch a native submit from
      // the injected fragment (it full-page POSTs to /cart), so post via AJAX.
      this.dialog.addEventListener('submit', (event) => {
        const form = event.target.closest && event.target.closest('[data-product-form]');
        if (form) this.onAddToCart(event, form);
      }, { signal });

      // Items appended by "Load more" need their checkbox state applied.
      this.root.addEventListener('collection:loadmore', () => this.syncCheckboxes(), { signal });

      this.mq.addEventListener('change', () => this.render(), { signal });

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.requestClose();
      }, { signal });
      this.dialog.addEventListener('close', () => this.onDialogClosed(), { signal });

      window.addEventListener('popstate', () => {
        if (this.hashHandles().length) {
          // Forward during the slide-out: the dialog is still `open`, so cancel
          // the close instead of letting it finish under a #compare= hash.
          if (this.closing) this.showDialog();
          else if (!this.dialog.open) this.openFromHash();
        } else if (this.dialog.open) {
          this.pushed = false;
          this.closeDialog();
        }
      }, { signal });
    }

    destroy() {
      if (!this.valid) return;
      this.abort.abort();
      if (this.fetchController) this.fetchController.abort();
      if (this.observer) this.observer.disconnect();
      window.clearTimeout(this.closeTimer);
      if (this.dialog.open) this.dialog.close();
      document.documentElement.classList.remove('compare-scroll-lock');
      document.body.classList.remove('compare-tray-visible');
      document.body.style.removeProperty('--compare-tray-height');
    }

    /* ── Checkboxes + tray ───────────────────────────────────────────────── */
    onToggle(input) {
      const handle = input.dataset.handle;
      if (input.checked) {
        if (this.items.length >= this.max || this.items.some((item) => item.handle === handle)) {
          this.syncCheckboxes();
          return;
        }
        this.items.push({
          handle,
          title: input.dataset.title || handle,
          image: input.dataset.image || '',
        });
        this.writeItems();
        this.render();
        const atCap = this.items.length >= this.max;
        this.announce(atCap
          ? fill(this.config.text.limitReached, { __MAX__: this.max })
          : this.selectedStatus());
      } else {
        this.removeItem(handle, { announce: true });
      }
    }

    removeItem(handle, { announce = false } = {}) {
      this.items = this.items.filter((item) => item.handle !== handle);
      this.writeItems();
      this.render();
      if (announce) this.announce(this.selectedStatus());
    }

    /* Rebuilding the tray destroys the focused chip button, so move focus to
       the chip now in that position, else the previous chip, else Compare (if
       enabled), else the removed product's card checkbox (tray may be hidden). */
    onChipRemove(chip) {
      const handle = chip.dataset.handle;
      const index = Array.from(this.slots.querySelectorAll('[data-compare-chip]')).indexOf(chip);
      this.removeItem(handle, { announce: true });

      const chips = this.slots.querySelectorAll('[data-compare-chip-remove]');
      let target = chips[index] || chips[index - 1] || null;
      if (!target && !this.openButton.disabled && !this.tray.hidden) target = this.openButton;
      if (!target) {
        target = Array.from(this.root.querySelectorAll('[data-compare-toggle]'))
          .find((input) => input.dataset.handle === handle) || null;
      }
      if (target) target.focus();
    }

    selectedStatus() {
      return fill(this.config.text.selectedStatus, {
        __SELECTED__: this.effective().length,
        __MAX__: this.max,
      });
    }

    /* showModal() makes the page behind the dialog inert, so while it is open
       messages go to the dialog's own live region. */
    announce(message) {
      const region = this.dialog.open ? this.dialogStatus : this.statusEl;
      if (!region) return;
      // Clear then set so identical consecutive messages are re-announced.
      region.textContent = '';
      window.requestAnimationFrame(() => {
        region.textContent = message;
      });
    }

    /* For messages tied to closing the dialog: announce once the page is no
       longer inert (see onDialogClosed). */
    closeWithAnnouncement(message) {
      this.pendingAnnouncement = message;
      this.requestClose();
    }

    render() {
      this.syncCheckboxes();
      this.renderTray();
    }

    syncCheckboxes() {
      const selected = new Set(this.items.map((item) => item.handle));
      const full = this.items.length >= this.max;
      this.root.querySelectorAll('[data-compare-toggle]').forEach((input) => {
        const checked = selected.has(input.dataset.handle);
        input.checked = checked;
        input.disabled = !checked && full;
      });
    }

    renderTray() {
      const shown = this.effective();
      const hasItems = this.items.length > 0;
      this.tray.hidden = !hasItems;
      document.body.classList.toggle('compare-tray-visible', hasItems);

      const fragment = document.createDocumentFragment();
      for (let i = 0; i < this.max; i += 1) {
        const item = shown[i];
        if (item) {
          const chip = this.chipTemplate.content.firstElementChild.cloneNode(true);
          chip.dataset.handle = item.handle;
          const image = chip.querySelector('.compare-chip__image');
          if (item.image) image.src = item.image;
          else image.remove();
          chip.querySelector('.compare-chip__title').textContent = item.title;
          chip.querySelector('[data-compare-chip-remove]')
            .setAttribute('aria-label', this.config.text.remove.split('__TITLE__').join(item.title));
          fragment.appendChild(chip);
        } else {
          fragment.appendChild(this.slotTemplate.content.firstElementChild.cloneNode(true));
        }
      }
      this.slots.replaceChildren(fragment);

      this.openButton.disabled = shown.length < 2;
      if (this.hintEl) {
        const hint = fill(this.config.text.hint, { __MAX__: this.max });
        this.hintEl.textContent = shown.length < 2 ? `${hint}. ${this.config.text.minHint}` : hint;
      }

      window.requestAnimationFrame(() => {
        document.body.style.setProperty(
          '--compare-tray-height',
          hasItems ? `${Math.ceil(this.tray.getBoundingClientRect().height)}px` : '0px'
        );
      });
    }

    /* ── Dialog ──────────────────────────────────────────────────────────── */
    hashHandles() {
      if (!window.location.hash.startsWith(HASH_PREFIX)) return [];
      let raw = window.location.hash.slice(HASH_PREFIX.length);
      try {
        raw = decodeURIComponent(raw);
      } catch {
        return [];
      }
      return Array.from(new Set(raw.split(',').map((h) => h.trim()).filter(Boolean)));
    }

    openFromHash() {
      const handles = this.hashHandles().slice(0, this.max);
      if (handles.length < 2) return;
      this.returnFocus = this.openButton;
      this.showDialog();
      this.fetchFragment(handles, { focus: 'title' });
    }

    open(trigger) {
      const handles = this.effective().map((item) => item.handle);
      if (handles.length < 2) return;
      this.returnFocus = trigger || document.activeElement;
      const hash = HASH_PREFIX + handles.join(',');
      if (window.location.hash !== hash) {
        window.history.pushState({ sdCompare: true }, '', hash);
        this.pushed = true;
      }
      this.showDialog();
      this.fetchFragment(handles, { focus: 'title' });
    }

    showDialog() {
      // Re-opened mid slide-out (e.g. browser Forward right after Back): cancel it.
      if (this.closing) this.finishClosing({ close: false });
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('compare-scroll-lock');
    }

    /* Back / Esc. With a pushed history entry, going back fires popstate which
       closes the dialog; otherwise close directly and drop the hash. */
    requestClose() {
      if (!this.dialog.open) return;
      if (this.pushed && window.location.hash.startsWith(HASH_PREFIX)) {
        window.history.back();
        return;
      }
      if (window.location.hash.startsWith(HASH_PREFIX)) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      this.closeDialog();
    }

    /* Slide out (CSS .is-closing) before closing; reduced motion closes at once.
       The timeout backs up animationend in case the animation never runs. */
    closeDialog() {
      if (this.fetchController) this.fetchController.abort();
      if (!this.dialog.open || this.closing) return;
      if (!this.motionQuery.matches) {
        this.dialog.close();
        return;
      }
      this.closing = true;
      this.dialog.classList.add('is-closing');
      this.onCloseAnimationEnd = (event) => {
        if (event.target === this.dialog) this.finishClosing();
      };
      this.dialog.addEventListener('animationend', this.onCloseAnimationEnd);
      this.closeTimer = window.setTimeout(() => this.finishClosing(), CLOSE_FALLBACK_MS);
    }

    finishClosing({ close = true } = {}) {
      window.clearTimeout(this.closeTimer);
      this.dialog.removeEventListener('animationend', this.onCloseAnimationEnd);
      this.dialog.classList.remove('is-closing');
      this.closing = false;
      if (close && this.dialog.open) this.dialog.close();
    }

    onDialogClosed() {
      if (this.observer) this.observer.disconnect();
      document.documentElement.classList.remove('compare-scroll-lock');
      this.body.replaceChildren();
      this.body.removeAttribute('aria-busy');
      const target = this.returnFocus && this.returnFocus.isConnected && !this.returnFocus.disabled
        ? this.returnFocus
        : this.root.querySelector('[data-compare-toggle]');
      if (target) target.focus();
      this.returnFocus = null;
      if (this.pendingAnnouncement) {
        this.announce(this.pendingAnnouncement);
        this.pendingAnnouncement = null;
      }
    }

    showState(message, { error = false } = {}) {
      this.state.hidden = false;
      this.stateMessage.textContent = message;
      this.retryButton.hidden = !error;
      this.dialogStatus.textContent = message;
    }

    hideState() {
      this.state.hidden = true;
      this.retryButton.hidden = true;
    }

    async fetchFragment(handles, options = {}) {
      this.lastRequest = { handles, options };
      if (this.fetchController) this.fetchController.abort();
      const controller = new AbortController();
      this.fetchController = controller;

      if (this.body.firstElementChild) {
        this.body.setAttribute('aria-busy', 'true');
        this.dialogStatus.textContent = this.config.text.loading;
      } else {
        this.showState(this.config.text.loading);
      }

      try {
        const query = `${this.collection}|${handles.join(',')}`;
        const url = `${this.config.root}search?q=${encodeURIComponent(query)}&section_id=${SECTION_ID}`;
        const response = await fetch(url, {
          signal: controller.signal,
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fragment = doc.querySelector('[data-compare-root]');
        if (!fragment) throw new Error('Malformed section response');

        const resolved = Array.from(fragment.querySelectorAll('[data-compare-cards] [data-compare-item]'))
          .map((el) => ({ handle: el.dataset.handle, title: el.dataset.title, image: el.dataset.image }));

        // Unpublished / missing products are silently dropped by the server:
        // prune them from the stored selection.
        const requested = new Set(handles);
        const rest = this.items.filter((item) => !requested.has(item.handle));
        const resolvedHandles = new Set(resolved.map((item) => item.handle));
        this.items = resolved.concat(rest.filter((item) => !resolvedHandles.has(item.handle)));
        this.writeItems();
        this.render();

        if (resolved.length < 2) {
          this.closeWithAnnouncement(this.config.text.empty);
          return;
        }

        this.body.replaceChildren(document.importNode(fragment, true));
        this.body.removeAttribute('aria-busy');
        this.hideState();
        this.dialogStatus.textContent = this.config.text.loaded;

        const newHash = HASH_PREFIX + resolved.map((item) => item.handle).join(',');
        if (window.location.hash !== newHash) {
          window.history.replaceState(window.history.state, '', newHash);
        }

        this.observeSticky();
        this.applyFocus(options);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.body.replaceChildren();
        this.body.removeAttribute('aria-busy');
        this.showState(this.config.text.error, { error: true });
        if (this.stateTitle) this.stateTitle.focus({ preventScroll: true });
      }
    }

    applyFocus({ focus, column, sticky } = {}) {
      let target = null;
      if (focus === 'select') {
        const scope = sticky ? '[data-compare-sticky]' : '[data-compare-cards]';
        target = this.body.querySelector(`${scope} [data-compare-select][data-column="${column}"]`);
      }
      if (!target) target = this.body.querySelector('[data-compare-title]');
      if (target) target.focus({ preventScroll: true });
    }

    observeSticky() {
      if (this.observer) this.observer.disconnect();
      const cards = this.body.querySelector('[data-compare-cards]');
      const sticky = this.body.querySelector('[data-compare-sticky]');
      if (!cards || !sticky || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        sticky.toggleAttribute('data-stuck', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 });
      this.observer.observe(cards);
    }

    onDialogRemove(handle) {
      this.removeItem(handle);
      const shown = this.effective();
      if (shown.length < 2) {
        this.closeWithAnnouncement(this.selectedStatus());
        return;
      }
      this.fetchFragment(shown.map((item) => item.handle), { focus: 'title' });
    }

    /* Same pattern as quick-view.js bindAddToCart (sanctioned Rebuy exception):
       POST the form to /cart/add.js, then close the dialog so Rebuy's Smart
       Cart drawer (opened by its /cart/add interception) isn't hidden behind
       the dialog's top layer. On failure, an inline alert and the button is
       restored. The selection stays in the tray. */
    async onAddToCart(event, form) {
      event.preventDefault();
      if (form.dataset.compareSubmitting === 'true') return;
      form.dataset.compareSubmitting = 'true';

      const submit = form.querySelector('[data-product-form-submit]');
      const label = submit ? submit.querySelector('[data-product-cta-label]') : null;
      const originalLabel = label ? label.textContent : '';
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
      }
      if (label) label.textContent = this.config.text.adding;
      const previousError = form.querySelector('[data-compare-form-error]');
      if (previousError) previousError.remove();

      try {
        const response = await fetch(`${this.config.root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new Error((data && data.description) || this.config.text.addError);
        this.returnFocus = this.openButton;
        this.requestClose();
      } catch (error) {
        const message = document.createElement('p');
        message.className = 'compare__form-error';
        message.setAttribute('data-compare-form-error', '');
        message.setAttribute('role', 'alert');
        message.textContent = (error && error.message) || this.config.text.addError;
        form.appendChild(message);
      } finally {
        form.dataset.compareSubmitting = 'false';
        if (submit) {
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
        }
        if (label) label.textContent = originalLabel;
      }
    }

    onSwap(select) {
      const oldHandle = select.dataset.current;
      const newHandle = select.value;
      if (!newHandle || newHandle === oldHandle) return;
      const option = select.selectedOptions[0];
      const next = {
        handle: newHandle,
        title: option ? option.textContent.trim() : newHandle,
        image: option ? option.dataset.image || '' : '',
      };
      // Drop any other copy first (e.g. a pick beyond the viewport cap), then
      // swap in place so column order is preserved.
      this.items = this.items.filter((item) => item.handle === oldHandle || item.handle !== newHandle);
      const index = this.items.findIndex((item) => item.handle === oldHandle);
      if (index === -1) this.items.push(next);
      else this.items[index] = next;
      this.writeItems();
      this.render();
      this.fetchFragment(this.effective().map((item) => item.handle), {
        focus: 'select',
        column: select.dataset.column,
        sticky: Boolean(select.closest('[data-compare-sticky]')),
      });
    }
  }

  /* ── Boot ──────────────────────────────────────────────────────────────── */
  let instance = null;

  function init() {
    const root = document.querySelector('[data-compare-collection]');
    if (instance && (!root || instance.root !== root || !instance.root.isConnected)) {
      instance.destroy();
      instance = null;
    }
    if (root && !instance) {
      const created = new CollectionCompare(root);
      if (created.valid) instance = created;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme-editor section reload / removal.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    if (instance && event.target && event.target.contains(instance.root)) {
      instance.destroy();
      instance = null;
    }
  });
})();
